import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/modules/growing-circle/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Camera, CheckCircle2, HelpCircle, XCircle, ArrowLeft, Upload, BookOpen, Tablet } from "lucide-react";

export default function AccessRequest() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const handoffToken = searchParams.get("h");
  const [step, setStep] = useState(1);
  const [editions, setEditions] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", edition: "", verification_type: "paperback", consent: false });
  const [requestId, setRequestId] = useState(null);
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [longWait, setLongWait] = useState(false);
  const [result, setResult] = useState(null);
  const [prefilled, setPrefilled] = useState(false);
  const fileRef = useRef();
  const submittingRef = useRef(false);

  const isKindle = form.verification_type === "kindle";

  useEffect(() => {
    api.get("/editions").then(({ data }) => setEditions(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!handoffToken) return;
    api.get(`/handoff/${handoffToken}`).then(({ data }) => {
      setForm((f) => ({ ...f, name: data.name || f.name, email: data.email || f.email }));
      setPrefilled(true);
    }).catch(() => {});
  }, [handoffToken]);

  const start = async () => {
    if (!form.name || !form.email || !form.edition) return toast.error("Please fill in all fields.");
    if (!form.consent) return toast.error("You must consent to the processing of your data.");
    setLoading(true);
    try {
      const { data } = await api.post("/access/start", { ...form, handoff_token: handoffToken || undefined });
      setRequestId(data.request_id);
      setStep(2);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) return toast.error("Please use a JPEG, PNG or WEBP image.");
    const reader = new FileReader();
    reader.onload = () => setImage(reader.result);
    reader.readAsDataURL(file);
  };

  const submitPhoto = async () => {
    if (!image) return toast.error("Please upload the image first.");
    if (submittingRef.current) return; // prevent duplicate submissions
    submittingRef.current = true;
    setLoading(true);
    setAnalyzing(true);
    setLongWait(false);
    const timer = setTimeout(() => setLongWait(true), 30000);
    try {
      const base64 = image.split(",")[1];
      const { data } = await api.post("/access/submit-photo", { request_id: requestId, image_base64: base64 });
      setResult(data);
      setStep(3);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      clearTimeout(timer);
      setLoading(false);
      setAnalyzing(false);
      submittingRef.current = false;
    }
  };

  return (
    <div className="min-h-screen bg-background grain py-10 px-5">
      <div className="max-w-xl mx-auto">
        <button onClick={() => navigate("/growing-circle")} className="text-sm text-muted-foreground flex items-center gap-1 mb-8 hover:text-foreground transition-colors" data-testid="back-home-btn">
          <ArrowLeft className="w-4 h-4" /> Back to the threshold
        </button>

        {step === 1 && (
          <div className="animate-fade-in-up">
            <h1 className="font-heading text-4xl tracking-tight mb-3">Request access</h1>
            <p className="text-muted-foreground mb-8 leading-relaxed">
              The space is reserved to those who own the book, in the paperback or Kindle edition. We'll ask you for a small gesture of trust.
            </p>
            <div className="space-y-6 bg-card border border-border rounded-3xl p-8">
              <div>
                <Label>First name and surname</Label>
                <Input data-testid="access-name-input" className="mt-2 rounded-xl bg-background" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <p className="text-xs text-muted-foreground mt-1.5">Your real first name and surname, as provided during registration.</p>
              </div>
              <div>
                <Label>Email</Label>
                <Input data-testid="access-email-input" type="email" className="mt-2 rounded-xl bg-background" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                <p className="text-xs text-muted-foreground mt-1.5">The email address used during registration.</p>
              </div>
              <div>
                <Label className="mb-3 block">Which edition do you own?</Label>
                <RadioGroup value={form.edition} onValueChange={(v) => setForm({ ...form, edition: v })} className="gap-3">
                  {editions.map((ed, i) => (
                    <label key={ed.id} className="flex items-center gap-3 border border-border rounded-xl p-4 cursor-pointer hover:bg-secondary/50 transition-colors" data-testid={`edition-option-${i}`}>
                      <RadioGroupItem value={ed.language} />
                      <span className="text-sm"><strong>{ed.language}</strong> — {ed.title}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>
              <div>
                <Label className="mb-3 block">How would you like to verify ownership?</Label>
                <RadioGroup value={form.verification_type} onValueChange={(v) => setForm({ ...form, verification_type: v })} className="grid grid-cols-2 gap-3">
                  <label className={`flex flex-col items-center gap-2 border rounded-xl p-4 cursor-pointer transition-colors ${!isKindle ? "border-primary bg-primary/5" : "border-border hover:bg-secondary/50"}`} data-testid="vtype-paperback">
                    <RadioGroupItem value="paperback" className="sr-only" />
                    <BookOpen className="w-6 h-6 text-primary" />
                    <span className="text-sm font-medium">Paperback</span>
                    <span className="text-xs text-muted-foreground text-center">Selfie with the book</span>
                  </label>
                  <label className={`flex flex-col items-center gap-2 border rounded-xl p-4 cursor-pointer transition-colors ${isKindle ? "border-primary bg-primary/5" : "border-border hover:bg-secondary/50"}`} data-testid="vtype-kindle">
                    <RadioGroupItem value="kindle" className="sr-only" />
                    <Tablet className="w-6 h-6 text-primary" />
                    <span className="text-sm font-medium">Kindle / eBook</span>
                    <span className="text-xs text-muted-foreground text-center">Screenshot of your library</span>
                  </label>
                </RadioGroup>
              </div>
              <label className="flex items-start gap-3 text-sm text-muted-foreground cursor-pointer">
                <Checkbox data-testid="access-consent-checkbox" checked={form.consent} onCheckedChange={(v) => setForm({ ...form, consent: !!v })} className="mt-0.5" />
                I consent to the processing of my data for verification. The image will be used only for this verification and deleted immediately after the decision, with no biometric recognition (GDPR).
              </label>
              <Button className="btn-gold w-full rounded-full h-12 text-sm font-medium" disabled={loading} onClick={start} data-testid="access-continue-btn">
                {loading ? "One moment…" : "Continue"}
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="animate-fade-in-up">
            {isKindle ? (
              <>
                <h1 className="font-heading text-4xl tracking-tight mb-3">Kindle verification</h1>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  Upload a <strong>screenshot</strong> or a photo of your screen where the book is clearly visible
                  in your <strong>Kindle library</strong>, or opened in the Kindle app/device.
                </p>
              </>
            ) : (
              <>
                <h1 className="font-heading text-4xl tracking-tight mb-3">Selfie with the book</h1>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  Take a <strong>selfie</strong> in which both your <strong>face</strong> and the <strong>book</strong> are clearly visible,
                  with the cover recognisable. No code or note is needed.
                </p>
              </>
            )}

            <div
              onClick={() => { if (!analyzing) fileRef.current?.click(); }}
              className={`border-2 border-dashed border-border rounded-3xl p-10 text-center bg-secondary/30 transition-colors ${analyzing ? "cursor-not-allowed" : "cursor-pointer hover:bg-secondary/50"}`}
              data-testid="photo-dropzone"
            >
              {image ? (
                <div className="relative inline-block overflow-hidden rounded-2xl">
                  <img src={image} alt="preview" className="max-h-64 mx-auto block" />
                  {analyzing && (
                    <>
                      <div className="scanner-overlay" />
                      <div className="scanner-line" data-testid="scanner-line" />
                    </>
                  )}
                </div>
              ) : (
                <div className="text-muted-foreground">
                  <Camera className="w-10 h-10 mx-auto mb-3" />
                  <p>{isKindle ? "Tap to upload the screenshot" : "Tap to upload or take the selfie"}</p>
                  <p className="text-xs mt-1">JPEG, PNG or WEBP</p>
                </div>
              )}
              <input ref={fileRef} type="file" accept="image/*" capture={isKindle ? undefined : "user"} className="hidden" onChange={onFile} disabled={analyzing} data-testid="photo-input" />
            </div>

            {analyzing && (
              <div className="mt-6 text-center bg-card border border-border rounded-3xl p-8 animate-fade-in-up" data-testid="analyzing-state" aria-live="polite">
                <div className="loader-ring mx-auto mb-5" />
                <h2 className="font-heading text-2xl tracking-tight mb-2">Analyzing your verification image…</h2>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {longWait
                    ? "This is taking a little longer than usual — we're still analysing your image. Please keep this page open."
                    : "This usually takes 10–30 seconds. Please keep this page open."}
                </p>
              </div>
            )}

            <Button className="btn-gold w-full mt-6 rounded-full h-12 text-sm font-medium" disabled={analyzing || !image} onClick={submitPhoto} data-testid="submit-photo-btn">
              {analyzing ? "Analyzing…" : (<><Upload className="w-4 h-4 mr-2" /> Submit for verification</>)}
            </Button>

            <p className="text-xs text-muted-foreground leading-relaxed mt-4 text-center" data-testid="activation-email-note">
              Once your copy is verified, we’ll email you a secure link to create your password and activate your access to the Self Doubling Growing Circle.
            </p>
          </div>
        )}

        {step === 3 && result && (
          <div className="animate-fade-in-up text-center bg-card border border-border rounded-3xl p-10" data-testid="verification-result">
            {result.status === "verified" && <CheckCircle2 className="w-16 h-16 mx-auto text-accent mb-5" />}
            {result.status === "doubtful" && <HelpCircle className="w-16 h-16 mx-auto text-primary mb-5" />}
            {result.status === "invalid" && <XCircle className="w-16 h-16 mx-auto text-primary mb-5" />}
            <h1 className="font-heading text-3xl tracking-tight mb-4">
              {result.status === "verified" ? "Welcome" : result.status === "doubtful" ? "Thank you, see you soon" : "Verification needed"}
            </h1>
            {result.status === "verified" ? (
              <div className="mb-8" data-testid="verified-activation-message">
                <p className="text-foreground leading-relaxed mb-3">
                  Your copy has been successfully verified.
                  <br />
                  We’ve sent you an email with a secure link to create your password and activate your access to the Self Doubling Growing Circle.
                </p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Please check your inbox. If you don’t see the email, check your spam or junk folder.
                </p>
              </div>
            ) : (
              <p className="text-muted-foreground leading-relaxed mb-8" data-testid="verification-rejection-message">
                We could not verify this image. You can try again with a new photo or contact the Self Doubling Growing Circle team at{" "}
                <a href="mailto:contact@selfdoubling.com" className="text-primary underline underline-offset-2 hover:text-primary/80">
                  contact@selfdoubling.com
                </a>{" "}
                for assistance.
              </p>
            )}
            {result.status === "invalid" && (
              <Button variant="outline" className="rounded-full mr-3" onClick={() => { setImage(null); setResult(null); setStep(2); }} data-testid="retry-btn">Try again</Button>
            )}
            <Button variant="outline" className="rounded-full" onClick={() => navigate("/growing-circle/login")} data-testid="result-login-btn">Go to sign in</Button>
          </div>
        )}
      </div>
    </div>
  );
}
